import { DateFormats, formatDate } from '@strata/helper-functions';
import { CollapsibleSection } from '@strata/react-collapsible-section';
import { createDateRange, DateRangeInputFormik } from '@strata/react-date-time';
import { DollarInputFormik, NumberInputFormik, TextInputFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import { FieldArray, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { AdditionalFields } from 'components/shared/AdditionalFields';
import {
    type ClaimDetails,
    useGetCategoryCodesQuery,
    useGetStatusCodesQuery,
} from 'store/api/medbillingApi';

const ValidationSchemaObject = {
    services: Yup.array(
        Yup.object({
            payerClaimNumber: Yup.string().required(validationStrings.requiredField.errorMessage),
            submittedAmount: Yup.number().required(validationStrings.requiredField.errorMessage),
            total: Yup.number().required(validationStrings.requiredField.errorMessage),
            procedureCode: Yup.string().required(validationStrings.requiredField.errorMessage),
            billingQty: Yup.number().required(validationStrings.requiredField.errorMessage),
            serviceCategoryCode: Yup.string().required(
                validationStrings.requiredField.errorMessage,
            ),
            serviceStatusCode: Yup.string().required(validationStrings.requiredField.errorMessage),
            dateOfService: DateRangeInputFormik.validator(
                undefined,
                dayjs().endOf('day').toDate(),
            ).required(validationStrings.requiredField.errorMessage),
            entityIdentifierCode: Yup.string(),
            secondaryServiceCategoryCode: Yup.string(),
            secondaryServiceStatusCode: Yup.string(),
        }),
    ).required(validationStrings.requiredField.errorMessage),
};
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ValidationSchema = Yup.object(ValidationSchemaObject);

interface UpdateClaimStatusServicesProps {
    payerId: string;
}

export const UpdateClaimStatusServices = ({ payerId }: UpdateClaimStatusServicesProps) => {
    const formik = useFormikContext<Yup.InferType<typeof ValidationSchema>>();

    const { data: categoryCodes, isLoading: categoryCodesLoading } = useGetCategoryCodesQuery({
        payerId,
    });
    const { data: statusCodes, isLoading: statusCodesLoading } = useGetStatusCodesQuery({
        payerId,
    });

    return (
        <FieldArray name="services">
            {(arrayHelpers) =>
                formik.values.services?.map((_, index) => {
                    const title = `Service ${index + 1}`;
                    const idPrefix = `${arrayHelpers.name}.${index}.`;
                    return (
                        <div
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                            className="padding__bottom--medium"
                        >
                            <CollapsibleSection
                                title={title}
                                renderExpanded={false}
                                content={
                                    <>
                                        <TextInputFormik
                                            id={`${idPrefix}payerClaimNumber`}
                                            label="Payer Claim Number"
                                            required
                                        />
                                        <DollarInputFormik
                                            id={`${idPrefix}submittedAmount`}
                                            label="Submitted Amount"
                                            required
                                        />
                                        <DollarInputFormik
                                            id={`${idPrefix}total`}
                                            label="Paid Amount"
                                            required
                                        />
                                        <TextInputFormik
                                            id={`${idPrefix}procedureCode`}
                                            label="Procedure Code"
                                            required
                                        />
                                        <NumberInputFormik
                                            id={`${idPrefix}billingQty`}
                                            label="Billing Quantity"
                                            required
                                        />
                                        <SuggestSingleSelectFormik
                                            id={`${idPrefix}serviceCategoryCode`}
                                            label="Service Category Code"
                                            required
                                            values={categoryCodes}
                                            isLoading={categoryCodesLoading}
                                        />
                                        <SuggestSingleSelectFormik
                                            id={`${idPrefix}serviceStatusCode`}
                                            label="Service Status Code"
                                            required
                                            values={statusCodes}
                                            isLoading={statusCodesLoading}
                                        />
                                        <DateRangeInputFormik
                                            name={`${idPrefix}dateOfService`}
                                            label="Date(s) Of Service"
                                            helperText="Single date range can be selected by entering same date for both."
                                            maxDate={dayjs().endOf('day').toDate()}
                                            required
                                        />
                                        <AdditionalFields title={title}>
                                            <TextInputFormik
                                                id={`${idPrefix}entityIdentifierCode`}
                                                label="Entity Identifier Code"
                                            />
                                            <SuggestSingleSelectFormik
                                                id={`${idPrefix}secondaryServiceCategoryCode`}
                                                label="Secondary Service Category Code"
                                                values={categoryCodes}
                                                isLoading={categoryCodesLoading}
                                            />
                                            <SuggestSingleSelectFormik
                                                id={`${idPrefix}secondaryServiceStatusCode`}
                                                label="Secondary Service Status Code"
                                                values={statusCodes}
                                                isLoading={statusCodesLoading}
                                            />
                                        </AdditionalFields>
                                    </>
                                }
                            />
                        </div>
                    );
                })
            }
        </FieldArray>
    );
};

UpdateClaimStatusServices.initialValues = (services?: ClaimDetails['services']) => ({
    services: services?.map((service) => ({
        ...service,
        dateOfService: createDateRange(service.dateOfService, service.dateOfServiceEnd),
    })),
});

UpdateClaimStatusServices.validationSchema = ValidationSchemaObject;

UpdateClaimStatusServices.submitValues = (values: Yup.InferType<typeof ValidationSchema>) => ({
    services: values.services.map((service) => ({
        payer_claim_number: service.payerClaimNumber,
        submitted_amount: service.submittedAmount,
        paid_amount: service.total,
        procedure_code: service.procedureCode,
        billing_quantity: service.billingQty,
        service_category_code: service.serviceCategoryCode,
        service_status_code: service.serviceStatusCode,
        date_of_service: formatDate(service.dateOfService[0]!, DateFormats.HYPHEN_DATE),
        date_of_service_end: formatDate(service.dateOfService[1]!, DateFormats.HYPHEN_DATE),
        entity_identifier_code: service.entityIdentifierCode,
        secondary_service_category_code: service.secondaryServiceCategoryCode,
        secondary_service_status_code: service.secondaryServiceStatusCode,
    })),
});
